import { Axios } from 'services/axios';

export const countryApi = {
  getAllCountries: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 50,
        isActive = null,
        search = '',
        sortBy = 'name',
        sortOrder = 'ASC',
        region = ''
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });

      if (isActive !== null) {
        params.append('isActive', String(isActive));
      }

      if (search) {
        params.append('search', search);
      }

      if (region) {
        params.append('region', region);
      }

      const response = await Axios.fetch(`/admin/country?${params}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      return responseData;
    } catch (error) {
      console.error('Error fetching countries:', error);

      let errorMessage = 'Failed to fetch countries';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getCountryById: async (id) => {
    try {
      if (!id) {
        throw new Error('Country ID is required');
      }

      const response = await Axios.fetch(`/admin/country/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const country = response.data.data || response.data;

      if (!country) {
        throw new Error('Country not found');
      }

      return country;
    } catch (error) {
      console.error('Error fetching country:', error);

      let errorMessage = 'Failed to fetch country';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Country not found';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getActiveCountries: async (options = {}) => {
    try {
      const response = await Axios.fetch(
        `public/countries/active?${
          options.isActive !== undefined ? `isActive=${options.isActive}&` : ''
        }${options.sortBy ? `sortBy=${options.sortBy}&` : ''}${
          options.sortOrder ? `sortOrder=${options.sortOrder}` : ''
        }`,
      );

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      return responseData;
    } catch (error) {
      console.error('Error fetching active countries:', error);

      let errorMessage = 'Failed to fetch active countries';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getPublicCountryById: async (id) => {
    try {
      if (!id) {
        throw new Error('Country ID is required');
      }

      const response = await Axios.fetch(`/public/country/${id}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const country = response.data.data || response.data;

      if (!country) {
        throw new Error('Country not found or not active');
      }

      return country;
    } catch (error) {
      console.error('Error fetching public country:', error);

      let errorMessage = 'Failed to fetch country';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Country not found or not active';
        } else {
          errorMessage =
            error.response.data?.message ||
            `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getDefaultCountry: async () => {
    try {
      const response = await Axios.fetch('/public/country/default');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const countryData = response.data.data || response.data;

      return countryData;
    } catch (error) {
      console.error('Error fetching default country:', error);

      let errorMessage = 'Failed to fetch default country';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  getRegions: async () => {
    try {
      const response = await Axios.fetch('/public/country/regions');

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const regionsData = response.data.data || response.data;

      return regionsData;
    } catch (error) {
      console.error('Error fetching regions:', error);

      let errorMessage = 'Failed to fetch regions';

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  // Placeholder functions for admin operations
  createCountry: async (countryData) => {
    // Will be implemented later
    console.log('Create country function - to be implemented');
    throw new Error('Create country functionality not implemented yet');
  },

  updateCountry: async (id, countryData) => {
    // Will be implemented later
    console.log('Update country function - to be implemented');
    throw new Error('Update country functionality not implemented yet');
  },

  deleteCountry: async (id) => {
    // Will be implemented later
    console.log('Delete country function - to be implemented');
    throw new Error('Delete country functionality not implemented yet');
  },

  formatCountryLabel: (country) => {
    if (!country) return '';
    
    if (country.displayName) return country.displayName;
    
    let label = country.name;
    if (country.countryCode) {
      label = `${country.name} (${country.countryCode})`;
    }
    
    return label;
  },

  getCountryFlagEmoji: (countryCode) => {
    if (!countryCode) return '';
    
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    
    return String.fromCodePoint(...codePoints);
  }
};
